import React, { useCallback, useMemo, useState } from 'react';
import { InstagramAudioStats } from '@round/api';
import { TabNav, TabNavItem, TabPanel, TabPanels, Tabs } from '@round/ui-kit';
import styles from './CreationsChart.module.css';
import { formatNumberToCommaNotation } from '../../../../../../helpers';
import LineChart, { Point } from '../../../../../../ui/DataDisplay/Charts/LineChart/LineChart';

type Props = {
    stats: InstagramAudioStats | undefined | null;
    isLoading?: boolean;
};

const CreationsChart = ({ isLoading, stats }: Props) => {
    const [activeTab, setActiveTab] = useState('total');

    const totalCreationsPoints: Point[] = useMemo(
        () =>
            stats?.reels_count_time_series.map((i) => ({
                x: i.time,
                y: i.value,
            })) ?? [],
        [stats?.reels_count_time_series]
    );

    const dailyCreationsPoints: Point[] = useMemo(
        () =>
            stats?.reels_count_time_series
                .filter((i) => typeof i.change === 'number')
                .map((i) => ({
                    x: i.time,
                    y: i.change!,
                }))
                .slice(1) ?? [],
        [stats?.reels_count_time_series]
    );

    const renderTooltip = useCallback(({ total, daily }: { total?: number; daily?: number }) => {
        return (
            <div className={styles.tooltipContent}>
                <span className={styles.tooltipDot} />
                <div>
                    {typeof total !== 'undefined' && (
                        <p>
                            Total Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(total)}</span>
                        </p>
                    )}
                    {typeof daily !== 'undefined' && (
                        <p>
                            Daily Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(daily)}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    }, []);

    const renderTotalCreationsTooltip = useCallback(
        (point: Point) => {
            const dailyCreations = dailyCreationsPoints.find((d) => d.x === point.x);
            return renderTooltip({ total: point.y, daily: dailyCreations?.y });
        },
        [dailyCreationsPoints, renderTooltip]
    );

    const renderDailyCreationsTooltip = useCallback(
        (point: Point) => {
            const totalCreations = totalCreationsPoints.find((t) => t.x === point.x);
            return renderTooltip({ total: totalCreations?.y, daily: point.y });
        },
        [renderTooltip, totalCreationsPoints]
    );

    return (
        <Tabs activeTab={activeTab} onChange={setActiveTab}>
            <TabNav className={styles.chartTabs}>
                <TabNavItem name="total">Total creations</TabNavItem>
                <TabNavItem name="daily">Daily creations</TabNavItem>
            </TabNav>

            <TabPanels>
                <TabPanel name="total">
                    <LineChart
                        title="Total Creations"
                        loading={isLoading}
                        points={totalCreationsPoints}
                        renderTooltip={renderTotalCreationsTooltip}
                    />
                </TabPanel>

                <TabPanel name="daily">
                    <LineChart
                        title="Daily Creations"
                        loading={isLoading}
                        points={dailyCreationsPoints}
                        renderTooltip={renderDailyCreationsTooltip}
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

export default CreationsChart;
